import React, { useEffect, useState, useMemo } from "react";
import config from "../../../config.ts";
import { useTranslation } from "react-i18next";
import { FaFilePdf } from "react-icons/fa"; // Import icons for buttons
import { generateICalendar } from "./ics";

const TeacherSchedule = () => {
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const apiUrl = config.apiUrl;
  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/Schedules/GetTeacherSchedule`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Network response was not ok");
        }
        const result = await response.json();
        setScheduleData(result.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, accessToken]);

  const handleExport = async (format: "pdf" | "excel") => {
    if (!accessToken) {
      alert("Access token is missing. Please log in.");
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/Schedules/GetCurrentTeacherSchedule`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to export schedule as ${format}.`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");

      let fileName = `Schedule_${new Date().toISOString()}`;
      if (format === "pdf") {
        fileName += ".pdf";
      }

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error(`Error exporting schedule as ${format}:`, error);
      alert(
        error.message ||
          `An unexpected error occurred while exporting schedule as ${format}.`
      );
    }
  };

  const handleExportPDF = () => {
    handleExport("pdf");
  };

  const weekDayNames: { [key: number]: string } = {
    1: t("Monday"),
    2: t("Tuesday"),
    3: t("Wednesday"),
    4: t("Thursday"),
    5: t("Friday"),
    6: t("Saturday"),
    7: t("Sunday"),
  };

  const getSpecialMarkers = (item: any) => {
    const markers = [];
    if (item.evenWeek === true)
      markers.push(<em style={{ fontWeight: "bold" }}>{t("even")}</em>);
    else if (item.evenWeek === false)
      markers.push(<em style={{ fontWeight: "bold" }}>{t("odd")}</em>);
    if (item.oneTimeEvent === true)
      markers.push(<em style={{ fontWeight: "bold" }}>*</em>);
    if (item.oneTimePeriodEvent === true)
      markers.push(<em style={{ fontWeight: "bold" }}>**</em>);
    return markers;
  };

  const getGroupInfo = (item: any) => {
    const parts = [`${item.specialityName}-${item.semester} сем.`];
    if (item.groupName) parts.push(`- ${item.groupName}`);
    if (item.subgroup) parts.push(`- ${item.subgroup}`);
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>{part} </React.Fragment>
        ))}
      </>
    );
  };

  const schedulesByDay = useMemo(() => {
    const filtered = scheduleData.filter((s: any) => s.weekDay != null);
    const grouped = filtered.reduce((acc: any, curr: any) => {
      const wd = curr.weekDay;
      if (!acc[wd]) acc[wd] = [];
      acc[wd].push(curr);
      return acc;
    }, {});

    const sortedKeys = Object.keys(grouped).sort(
      (a, b) => Number(a) - Number(b)
    );
    return sortedKeys.map((k) => ({ day: parseInt(k), items: grouped[k] }));
  }, [scheduleData]);

  const oneTimeItems = scheduleData.filter(
    (s: any) => s.oneTimeEvent === true || s.oneTimePeriodEvent === true
  );

  if (loading) return <div className="text-center">Loading...</div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2">
          <button
            onClick={handleExportPDF}
            className="flex items-center bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            <FaFilePdf className="mr-2" /> {t("Export to PDF")}
          </button>
          <button
            onClick={() => generateICalendar(scheduleData)}
            className="bg-blue-500 text-white py-2 px-4 rounded"
          >
            {t("Export to Calendar")}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="text-right my-4">
        <table className="inline-block border-collapse">
          <thead>
            <tr>
              <th className="font-bold text-left">{t("Legend")}:</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <em className="font-bold">* </em> {t("One-time event")}
              </td>
            </tr>
            <tr>
              <td>
                <em className="font-bold">** </em> {t("Event is held within the specified period")}
              </td>
            </tr>
            <tr>
              <td>
                <em className="font-bold">чет. </em> {t("Event is held only in even weeks")}
              </td>
            </tr>
            <tr>
              <td>
                <em className="font-bold">неч. </em> {t("Event is held only in odd weeks")}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Main Table (Grouped by Day) */}
      <table className="w-full border-collapse mb-4">
        <thead className="bg-gray-300 dark:bg-gray-700">
          <tr>
            <th className="border p-2">{t("Day")}</th>
            <th className="border p-2">{t("Subject")}</th>
            <th className="border p-2">{t("Type")}</th>
            <th className="border p-2">{t("Time")}</th>
            <th className="border p-2">{t("Hall")}</th>
            <th className="border p-2">{t("Speciality")}</th>
            <th className="border p-2">{t("Weeks")}</th>
          </tr>
        </thead>
        <tbody>
          {schedulesByDay.length > 0 ? (
            schedulesByDay.map(({ day, items }, dayIndex) => {
              const dayName = weekDayNames[day] || `Ден ${day}`;
              let isFirstRow = true;
              return items.map((subject: any, idx: number) => (
                <tr key={idx}>
                  {isFirstRow && (
                    <td
                      rowSpan={items.length}
                      className="border p-2 text-center font-bold whitespace-nowrap"
                    >
                      {dayName}
                    </td>
                  )}
                  <td className="border p-2">{subject.subjectName}</td>
                  <td className="border p-2">
                    {(subject.activityTypeName || subject.activityType) + " "}
                    {getSpecialMarkers(subject).map((m, i) => (
                      <React.Fragment key={i}>{m} </React.Fragment>
                    ))}
                  </td>
                  <td className="border p-2">
                    {subject.startHour}-{subject.endHour}
                  </td>
                  <td className="border p-2">{subject.hallNumber}</td>
                  <td className="border p-2">{getGroupInfo(subject)}</td>
                  <td className="border p-2">
                    {subject.startWeek && subject.endWeek ? (
                      <>
                        {subject.startWeek} - {subject.endWeek}
                      </>
                    ) : subject.evenWeek != null ? (
                      <>{subject.evenWeek === true ? t("even") : t("odd")}</>
                    ) : (
                      <>всяка</>
                    )}
                  </td>
                  {(() => {
                    isFirstRow = false;
                  })()}
                </tr>
              ));
            })
          ) : (
            <tr>
              <td colSpan={7} className="py-2 px-4 text-center">
                {t("No data available")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* One-time / One-time period events table */}
      {oneTimeItems.length > 0 && (
        <table className="w-full border-collapse">
          <thead className="bg-gray-300 dark:bg-gray-700">
            <tr>
              <th className="border p-2">{t("Dates")}</th>
              <th className="border p-2">{t("Subject")}</th>
              <th className="border p-2">{t("Type")}</th>
              <th className="border p-2">{t("Time")}</th>
              <th className="border p-2">{t("Hall")}</th>
              <th className="border p-2">{t("Speciality")}</th>
            </tr>
          </thead>
          <tbody>
            {oneTimeItems.map((otEvent: any, i: number) => (
              <tr key={i}>
                <td className="border p-2 text-center font-bold">
                  {getSpecialMarkers(otEvent).map((m, idx) => (
                    <React.Fragment key={idx}>{m} </React.Fragment>
                  ))}
                  {otEvent.oneTimePeriodEvent ? (
                    <>
                      {otEvent.startDate
                        ? new Date(otEvent.startDate).toLocaleDateString(
                            "bg-BG"
                          )
                        : ""}{" "}
                      -
                      {otEvent.endDate
                        ? new Date(otEvent.endDate).toLocaleDateString("bg-BG")
                        : ""}
                    </>
                  ) : (
                    <>
                      {otEvent.startDate
                        ? new Date(otEvent.startDate).toLocaleDateString(
                            "bg-BG"
                          )
                        : ""}
                    </>
                  )}
                </td>
                <td className="border p-2">{otEvent.subjectName}</td>
                <td className="border p-2">
                  {otEvent.activityTypeName || otEvent.activityType}
                </td>
                <td className="border p-2">
                  {otEvent.startHour}-{otEvent.endHour}
                </td>
                <td className="border p-2">{otEvent.hallNumber}</td>
                <td className="border p-2">
                  {otEvent.oneTimePeriodEvent ? (
                    <>{otEvent.specialityName}</>
                  ) : (
                    <>{getGroupInfo(otEvent)}</>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TeacherSchedule;
